import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { TestSignalPathSchema } from "../g2-fleet-messages/test-signal-metadata";
import { FEATURE_NAME } from "../feature-name";

/**
 * The delivery legs a test signal can exercise.
 *
 * Re-exported from the wire metadata contract rather than restated, so the
 * legs the API accepts cannot drift from the ones a stored clip can be tagged
 * with. Adding a leg is a one-line change there; every exhaustive `match` on
 * this type then fails to compile until it handles the new one.
 *
 * `TEST_SIGNAL_PATHS` is derived from the schema so the UI can offer one action per
 * leg without restating the list either.
 */
export const TestSignalPath = TestSignalPathSchema;
export type TestSignalPath = z.output<typeof TestSignalPath>;
export const TEST_SIGNAL_PATHS = TestSignalPath.options;

/**
 * Defaulted for the same reason the command defaults it: a client that omits
 * the field means the camera leg, which is the only one every camera has.
 */
export const TriggerTestSignalBody = z.object({
  signalPath: TestSignalPath.default("camera"),
});
export type TriggerTestSignalBody = z.output<typeof TriggerTestSignalBody>;

/**
 * The publish receipt, matching `TestSignalTriggerReceipt` from
 * `../client-services/test-signals-service`.
 *
 * `topic` and `publishedAt` are what correlate the request with the
 * `"test signal command received"` line on the device. Neither proves the
 * camera acted — see the receipt type's own doc comment.
 */
export const TriggerTestSignalResponse = z.object({
  topic: z.string(),
  publishedAt: z.string(),
});
export type TriggerTestSignalResponse = z.output<typeof TriggerTestSignalResponse>;

/**
 * Camera-scoped — the scope `defineResource` defaults to. The command topic
 * addresses one installed camera, so the camera belongs in the path rather
 * than the body (ADR-0031).
 */
export const testSignalsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "test-signals",
});

export const triggerTestSignalEndpoint = defineEndpoint(testSignalsResource, {
  method: "POST",
  bodySchema: TriggerTestSignalBody,
  responseSchema: TriggerTestSignalResponse,
});
